import _ from 'lodash';
//services
import { changeBeneficiaryStatus, createBeneficiary, buildCategoryGroups } from 'services/beneficiary.services';
//reducers
import { selectTranslations } from 'state/reducers/translation.reducers';
//utils
import { createCategoryMap } from 'utils/paymentCategoryUtils';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import {
	getNewBeneficiary,
	getMessage,
	setActiveStep,
	getBeneficiaryDetails,
	getBeneficiaryBankDetails,
	getPaymentCategoryOptions,
	getPaymentOptions,
	setMessage,
	setBeneficiary,
} from 'state/reducers/beneficiary.reducers';
import { getCountries, getCurrencies } from 'state/reducers/global.reducers';
//components
import { FormsStack } from 'theme/elements/Forms';
import { InfoCard, LightGreenCard } from 'theme/elements/Cards';
import { FormHeader1, FormHeadWithCheckbox } from 'theme/elements/Headers';
import { Paragraph3 } from 'theme/elements/Paragraphs';
import WarningDialog from 'components/WarningDialog';
//sections
import Footer from '../Footer';
//mui
import { Checkbox, Grid, Stack, TextField, Typography, useTheme } from '@mui/material';
//constants
import {
	Beneficiary,
	BeneficiaryStatus,
	beneficiarySteps,
	beneficiaryType,
	paymentsMethodsMapping,
	PaymentCategoryOption,
} from 'constants/beneficiary.constants';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { userMessage } from 'constants/ui.constants';
import { commentLabel, fileLabel, prepareBeneficiaryForCreation, formalBankApproveFileLabel } from '../../helper';
import { CustomCheckbox } from 'theme/elements/Inputs';
import { getUserPermission } from 'state/reducers/user.reducers';
import { UserPermission } from 'constants/user.constants';
import { Download } from '@mui/icons-material';
import { getFile } from 'services/global.services';
import PaymentCategoryDisplay from 'components/PaymentCategoryDisplay';

export default function Step3(props: { handleCancel: any; isNew?: boolean }) {
	const beneficiary: Beneficiary = useAppSelector(getNewBeneficiary);
	const userMessage: userMessage = useAppSelector(getMessage);
	const beneficiaryDetails = useAppSelector(getBeneficiaryDetails);
	const beneficiaryBankDetails = useAppSelector(getBeneficiaryBankDetails);
	const categoryOptions = useAppSelector(getPaymentCategoryOptions);

	const Countries = _.keyBy(useAppSelector(getCountries), 'id');
	const Currencies = _.keyBy(useAppSelector(getCurrencies), 'id');
	const paymentOptions = useAppSelector(getPaymentOptions);
	const userPermission = useAppSelector(getUserPermission);
	const prefixFields = 'page.beneficiary.create.step.3.';
	const prefixFields1 = `page.beneficiary.create.step.1.beneficiary_details.`;

	const intersect = (type) => {
		let obj: Partial<Beneficiary> = {};
		try {
			let gp = _.groupBy(paymentOptions, 'type');
			if (_.get(gp, type)) {
				_.values(gp[type]).forEach((pay) => {
					if (pay.optional) {
						pay.fields.forEach((field) => {
							obj[field] = _.get(beneficiary, paymentsMethodsMapping[field]);
						});
					}
				});
			}
		} catch (e) {}
		return obj;
	};
	const localPayment = intersect('regular');
	const priorityPayment = intersect('priority');
	const [agree, setAgree] = useState(
		!_.includes([UserPermission.SIGNER, UserPermission.OPERATIONER], userPermission)
	);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const t = useAppSelector(selectTranslations);

	const categoryMap = useMemo(() => createCategoryMap(categoryOptions), [categoryOptions]);

	const groupedCategories = useMemo(
		() => buildCategoryGroups(beneficiary.paymentCategories, categoryMap),
		[beneficiary.paymentCategories, categoryMap]
	);

	function formatValue(value: string, key: string) {
		switch (key) {
			case 'countryId':
			case 'bankAccountCountryId':
				return _.get(Countries[value], 'name') || '';
			case 'currencyId':
				return _.get(Currencies[value], 'name') || '';

			case 'formalBankApproveFile':
				return decodeURI(_.get(beneficiary, 'formalBankApproveFile.originalFileName'));
			default:
				return value;
		}
	}

	const beneficiaryDetailsCalc = useMemo(() => {
		let { firstName, lastName, identificationNumber, ...beneficiaryObj } = beneficiaryDetails;
		let objectEntries;
		if (beneficiary.type === beneficiaryType.COMPANY) {
			objectEntries = Object.entries(beneficiaryObj);
			objectEntries.splice(objectEntries.findIndex((val) => val[0] === 'nickname') + 1, 0, [
				'companyNumber',
				identificationNumber,
			]);
		} else {
			objectEntries = Object.entries(beneficiaryObj);
			const idx = objectEntries.findIndex((val) => val[0] === 'nickname');
			objectEntries.splice(idx + 1, 0, ['identificationNumber', identificationNumber]);
			objectEntries.splice(idx, 0, ['last_name', lastName]);
			objectEntries.splice(idx, 0, ['first_name', firstName]);
		}
		beneficiaryObj = Object.fromEntries(objectEntries);
		return beneficiaryObj;
	}, [beneficiaryDetails]);

	const openDialog = useMemo(() => {
		return userMessage.exist;
	}, [userMessage]);

	const isPendingSigning = useMemo(() => {
		return (
			_.includes(UserPermission.SIGNER, userPermission) && beneficiary.status === BeneficiaryStatus.PendingSigning
		);
	}, [beneficiary, userPermission]);

	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				title={t(userMessage.text || '')}
				subTitle={userMessage.subText}
				handleCancel={() => {
					dispatch(setMessage({ exist: false }));
				}}
			/>
			<FormsStack>
				<InfoCard>
					<FormHeader1 title={t(`${prefixFields}beneficiary_details.title`)} />
					<Stack spacing={2}>
						{_.keys(beneficiaryDetailsCalc).map((field) => {
							return (
								<Paragraph3
									key={field}
									color={theme.palette.root.basicDeepBlue}
									title={t(`${prefixFields}beneficiary_details.${_.snakeCase(field)}.text`)}
									text={formatValue(_.get(beneficiaryDetailsCalc, field), field)}
								/>
							);
						})}
						{beneficiary.website && (
							<Paragraph3
								color={theme.palette.root.basicDeepBlue}
								title={t(`${prefixFields1}website.text`)}
								text={formatValue(_.get(beneficiary, 'website') || '', 'website')}
							/>
						)}
						{beneficiary?.formalBankApproveFile && (
							<Stack flexDirection={'row'}>
								<Paragraph3
									color={theme.palette.root.basicDeepBlue}
									title={t(formalBankApproveFileLabel)}
									text={formatValue(
										_.get(beneficiaryDetailsCalc, 'formalBankApproveFile'),
										'formalBankApproveFile'
									)}
								/>
								<Download
									cursor={'pointer'}
									onClick={() =>
										dispatch(
											getFile(
												_.get(beneficiary, 'formalBankApproveFile.key'),
												Number(beneficiary.companyId)
											)
										)
									}
								/>
							</Stack>
						)}
						{beneficiary?.files && beneficiary.files.length > 0 && (
							<Stack spacing={1}>
								{beneficiary.files.map((file, index) => (
									<Stack key={index} flexDirection={'row'} alignItems="center" gap={1}>
										<Paragraph3
											color={theme.palette.root.basicDeepBlue}
											title={t(fileLabel)}
											text={
												file.originalFileName
													? decodeURI(file.originalFileName)
													: `File ${index + 1}`
											}
										/>
										<Download
											cursor={'pointer'}
											onClick={() => dispatch(getFile(file.key, Number(beneficiary.companyId)))}
										/>
									</Stack>
								))}
							</Stack>
						)}
						{beneficiary?.comment && (
							<Paragraph3
								color={theme.palette.root.basicDeepBlue}
								title={t(commentLabel)}
								text={_.get(beneficiary, 'comment')}
							/>
						)}
					</Stack>
				</InfoCard>
				<InfoCard>
					<FormHeader1 title={t(`${prefixFields}beneficiary_bank_details.title`)} />
					<Stack spacing={2}>
						{_.keys(beneficiaryBankDetails).map((field) => {
							return (
								<Paragraph3
									key={field}
									color={theme.palette.root.basicDeepBlue}
									title={t(`${prefixFields}beneficiary_bank_details.${_.snakeCase(field)}.text`)}
									text={formatValue(_.get(beneficiaryBankDetails, field), field)}
								/>
							);
						})}
						{_.keys(localPayment).length > 0 && (
							<LightGreenCard>
								<Grid flexDirection="column" container display="flex">
									<Stack spacing={2}>
										<Typography color={theme.palette.root.ux07777} variant="caption">
											{t(`${prefixFields}regular.text`)}
										</Typography>
										{_.keys(localPayment).map((field) => {
											return (
												<Paragraph3
													color={theme.palette.root.basicDeepBlue}
													key={field}
													title={t(`${prefixFields}payment_method.${field}.text`)}
													text={_.get(localPayment, field)}
												/>
											);
										})}
									</Stack>
								</Grid>
							</LightGreenCard>
						)}
						{_.keys(priorityPayment).length > 0 && (
							<LightGreenCard>
								<Grid flexDirection="column" container display="flex">
									<Stack spacing={2}>
										<Typography color={theme.palette.root.ux07777} variant="caption">
											{t(`${prefixFields}priority.text`)}
										</Typography>
										{_.keys(priorityPayment).map((field) => {
											return (
												<Paragraph3
													color={theme.palette.root.basicDeepBlue}
													key={field}
													title={t(`${prefixFields}payment_method.${field}.text`)}
													text={_.get(priorityPayment, field)}
												/>
											);
										})}
									</Stack>
								</Grid>
							</LightGreenCard>
						)}
					</Stack>
				</InfoCard>
				<InfoCard>
					<FormHeader1 title={t('page.beneficiary.create.step.3.payment_categories.title')} />
					{(beneficiary.paymentCategories || []).length === 0 ? (
						<Typography color={theme.palette.root.basicDeepBlue} variant="body2">
							{t('page.beneficiary.create.step.3.payment_categories.empty')}
						</Typography>
					) : (
						<PaymentCategoryDisplay
							primOrder={groupedCategories.primOrder}
							groups={groupedCategories.groups}
						/>
					)}
				</InfoCard>
				<>
					{beneficiary?.status && (
						<InfoCard>
							<FormHeader1 title={t(`${prefixFields}beneficiary_details.message.text`)} />
							<TextField
								multiline
								rows={4}
								variant="outlined"
								fullWidth
								name="userEditComment"
								value={beneficiary.userEditComment}
								inputProps={{ maxLength: 200 }}
								onChange={(e) => {
									dispatch(
										setBeneficiary({
											name: 'userEditComment',
											value: e.target.value,
										})
									);
								}}
							/>
						</InfoCard>
					)}
				</>
				<>
					{_.includes([UserPermission.SIGNER, UserPermission.OPERATIONER], userPermission) && (
						<InfoCard>
							<FormHeadWithCheckbox>
								<CustomCheckbox>
									<Checkbox
										sx={{ padding: 0, alignItems: 'start' }}
										onChange={(event) => {
											setAgree(event.target.checked);
										}}
										checked={agree}
									/>
								</CustomCheckbox>
								<Paragraph3
									title={t(`${prefixFields}disclaimer.title`)}
									text={t(`${prefixFields}disclaimer.text`)}
								/>
							</FormHeadWithCheckbox>
						</InfoCard>
					)}
				</>

				<Footer
					handleCancel={props.handleCancel}
					handleBack={() => {
						dispatch(setActiveStep(beneficiarySteps.BANK_ACCOUNT));
					}}
					ifBack={true}
					disableBack={isPendingSigning}
					disableSubmit={!agree}
					labelSubmit={
						isPendingSigning ? t(`${prefixFields}sign_button.text`) : t(`${prefixFields}create_button.text`)
					}
					labelBack={t(`${prefixFields}back_button.text`)}
					labelCancel={t(`${prefixFields}cancel_button.text`)}
					handleSubmit={() => {
						const bn = prepareBeneficiaryForCreation(beneficiary, paymentOptions);
						if (isPendingSigning) {
							dispatch(changeBeneficiaryStatus(beneficiary.id!, BeneficiaryStatus.Approved));
							dispatch(setActiveStep(beneficiarySteps.COMPLETE));
						} else {
							dispatch(createBeneficiary(bn, props.isNew));
						}
					}}
				/>
			</FormsStack>
		</Fragment>
	);
}
